import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress,
  Box,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  SelectChangeEvent,
} from "@mui/material";
import { useMutation, useQuery } from "@tanstack/react-query";
import { useApi } from "@platform/api";
import { Alert, Button, TextField } from "@platform/design-system";

import { AnalystTeam, AnalystTeamLead } from "../types";

interface TeamFormProps {
  open: boolean;
  onClose: () => void;
  onSuccess: () => void;
  team?: AnalystTeam | null;
}

interface CreateTeamRequest {
  name: string;
  description?: string | null;
  teamLeadId?: number | null;
}

interface UpdateTeamRequest {
  name?: string;
  description?: string | null;
  teamLeadId?: number | null;
}

export const TeamForm: React.FC<TeamFormProps> = ({
  open,
  onClose,
  onSuccess,
  team,
}) => {
  const isEditing = !!team;
  const axiosInstance = useApi();

  // Fetch team leads for the dropdown
  const { data: teamLeads = [], isLoading: teamLeadsLoading } = useQuery<
    AnalystTeamLead[],
    Error
  >({
    queryKey: ["teamLeads"],
    queryFn: async () => {
      const response = await axiosInstance.get<AnalystTeamLead[]>(
        "brokerportal/v1/analysts/team-leads",
      );
      return response.data;
    },
  });

  const [formData, setFormData] = useState({
    name: "",
    description: "",
    teamLeadId: undefined as number | null | undefined,
  });

  useEffect(() => {
    if (isEditing && team) {
      setFormData({
        name: team.name ?? "",
        description: team.description ?? "",
        teamLeadId: team.teamLeadId ?? null,
      });
    } else if (!isEditing) {
      setFormData({
        name: "",
        description: "",
        teamLeadId: null,
      });
    }
  }, [isEditing, team, open]);

  const createMutation = useMutation<
    unknown,
    Error,
    CreateTeamRequest,
    unknown
  >({
    mutationFn: (data: CreateTeamRequest) =>
      axiosInstance.post("brokerportal/v1/analysts/teams", data),
    onSuccess: () => {
      onSuccess();
    },
  });

  const updateMutation = useMutation<
    unknown,
    Error,
    UpdateTeamRequest,
    unknown
  >({
    mutationFn: (data: UpdateTeamRequest) =>
      axiosInstance.patch(`brokerportal/v1/analysts/teams/${team!.id}`, data),
    onSuccess: () => {
      onSuccess();
    },
  });

  const handleInputChange =
    (field: string) =>
    (event: React.ChangeEvent<HTMLInputElement>): void => {
      setFormData((prev) => ({ ...prev, [field]: event.target.value }));
    };

  const handleTeamLeadChange = (
    event: SelectChangeEvent<string | number>,
  ): void => {
    const value = event.target.value === "" ? null : Number(event.target.value);
    setFormData((prev) => ({ ...prev, teamLeadId: value }));
  };

  const handleSubmit = (event: React.FormEvent): void => {
    event.preventDefault();

    if (isEditing) {
      const updateData: UpdateTeamRequest = {
        name: formData.name,
        description: formData.description === "" ? "" : formData.description,
        teamLeadId: formData.teamLeadId,
      };
      updateMutation.mutate(updateData);
    } else {
      const createData: CreateTeamRequest = {
        name: formData.name,
        description:
          formData.description === "" ? undefined : formData.description,
        teamLeadId: formData.teamLeadId,
      };
      createMutation.mutate(createData);
    }
  };

  const handleClose = (): void => {
    setFormData({
      name: "",
      description: "",
      teamLeadId: null,
    });
    onClose();
  };

  const isLoading = createMutation.isPending || updateMutation.isPending;
  const error = createMutation.error || updateMutation.error;

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>{isEditing ? "Edit Team" : "Add New Team"}</DialogTitle>
      <form onSubmit={handleSubmit}>
        <DialogContent>
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error instanceof Error ? error.message : "An error occurred"}
            </Alert>
          )}

          <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
            <TextField
              label="Team Name"
              fullWidth
              required
              value={formData.name}
              onChange={handleInputChange("name")}
              helperText="Enter a descriptive name for the team"
            />

            <TextField
              label="Description"
              fullWidth
              multiline
              rows={4}
              value={formData.description}
              onChange={handleInputChange("description")}
              helperText="Optional description of the team's purpose or responsibilities"
            />

            <FormControl fullWidth>
              <InputLabel id="team-lead-label">Team Lead</InputLabel>
              <Select
                labelId="team-lead-label"
                value={formData.teamLeadId ?? ""}
                label="Team Lead"
                onChange={handleTeamLeadChange}
                disabled={teamLeadsLoading}
              >
                <MenuItem value="">
                  <em>No Team Lead</em>
                </MenuItem>
                {teamLeads.map((lead) => (
                  <MenuItem key={lead.id} value={lead.id}>
                    {lead.givenName} {lead.familyName} ({lead.email})
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
        </DialogContent>

        <DialogActions>
          <Button variant="primary" onClick={handleClose} disabled={isLoading}>
            Cancel
          </Button>
          <Button type="submit" variant="primary" disabled={isLoading}>
            {isLoading && <CircularProgress size={20} sx={{ mr: 1 }} />}
            {isEditing ? "Update" : "Create"} Team
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};
